import { Component, Input, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Chart, Color, registerables } from 'chart.js';
import 'chartjs-adapter-date-fns';
import zoomPlugin from 'chartjs-plugin-zoom';
import { ButtonModule } from 'primeng/button';
import { ChartModule, UIChart } from 'primeng/chart';
import { PanelModule } from 'primeng/panel';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SelectButtonModule } from 'primeng/selectbutton';
import { GraphGroup, GraphItem } from '../../models/graphItem';
import { VerticalLinePlugin } from '../../utilities/vertical-line.plugin';

Chart.register(...registerables, VerticalLinePlugin);

@Component({
  selector: 'app-groupedgraph',
  imports: [
    FormsModule,
    PanelModule,
    ProgressSpinnerModule,
    ChartModule,
    ButtonModule,
    SelectButtonModule,
    TranslatePipe
  ],
  templateUrl: './groupedgraph.component.html',
  styleUrl: './groupedgraph.component.css'
})
export class GroupedgraphComponent {
  measures: GraphItem[] = []

  private _verticalLineDate: number = 0;

  @Input() loading: boolean = false;

  @Input() set grafico(value: GraphGroup) {
    this.translateService.get(value.title).subscribe(t => {
      this.title = t;
    });
    this.measures = [...value.signals];

    this.options.scales.y.title.text = value.uom;

    this.options.scales.x.min = value.xMin;
    this.options.scales.x.max = value.xMax;

    this.options.scales.y.min = value.yMin;
    this.options.scales.y.max = value.yMax;

    this.options.plugins!['verticalLine'].value = value.xMin;

    this.data.datasets = [];
    value.signals.forEach(s => {
      if (s.signal.length > 0) {
        this.translateService.get(s.deviceType).subscribe(t => {
          this.data.datasets.push({
            label: t,
            data: s.signal,
            fill: false,
            borderColor: this.getcolor(s.deviceType),
            pointRadius: 0
          })
        })
      }
    });
  }

  @Input() set timestamp(value: number) {
    this._verticalLineDate = value;
    this.options.plugins!['verticalLine'].value = value;
    this.myChartRef?.chart.update();
  }

  title: string = '';
  data: any;
  options: any;
  plugins: any[] = [];

  @ViewChild('chart') myChartRef: UIChart | undefined;

  modeOptions: any[] = [
    { icon: 'fas fa-magnifying-glass', value: 1 },
    { icon: 'far fa-hand', value: 2 }
  ];
  mode: number = 1;

  constructor(private translateService: TranslateService) {
    this.plugins = [zoomPlugin];

    this.data = {
      datasets: []
    }

    this.options = {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          type: 'time',
          time: {
            minUnit: 'millisecond',
            tooltipFormat: 'HH:mm:ss.SSS',
            displayFormats: {
              minute: 'HH:mm:ss',
              second: 'HH:mm:ss',
              millisecond: 'HH.mm.ss.SSS'
            }
          },
          ticks: {
            source: 'auto'
          }
        },
        y: {
          type: 'linear',
          title: {
            display: true,
            text: ''
          },
          ticks: {
            precision: 0
          }
        }
      },
      plugins: {
        legend: {
          display: true
        },
        verticalLine: {
          value: this._verticalLineDate,
          color: 'red',
          width: 3
        },
        zoom: {
          pan: {
            enabled: false,
            mode: 'xy',
          },
          zoom: {
            wheel: {
              enabled: true,
            },
            pinch: {
              enabled: true,
            },
            drag: {
              enabled: true,
              borderWidth: 4
            },
            mode: 'x',
            scaleMode: 'xy',
          }
        }
      }
    };
  }

  getcolor(type: string): string {
    switch (type) {
      case 'head':
        return 'rgb(54, 162, 235)';
      case 'left':
        return 'rgb(255, 159, 64)';
      case 'right':
        return 'rgb(75, 192, 75)';
      default:
        return 'rgb(153, 102, 255)';
    }
  }

  onModeSelection(): void {
    const useZoom: boolean = this.mode === 1;
    const usePan: boolean = this.mode === 2;

    this.options.plugins.zoom.pan.enabled = usePan;
    this.options.plugins.zoom.zoom.wheel.enabled = useZoom;
    this.options.plugins.zoom.zoom.pinch.enabled = useZoom;
    this.options.plugins.zoom.zoom.drag.enabled = useZoom;

    this.myChartRef?.chart.update();
  }

  onZoomIn() {
    this.myChartRef?.chart.zoom(1.2);
  }

  onZoomOut() {
    this.myChartRef?.chart.zoom(0.8);
  }

  onZoomReset() {
    this.myChartRef?.chart.resetZoom();
  }

}
